import { GAMIFICATION_LEVEL_COUNT } from '@communities/common-layer/constants'
import type {
  FormatGamificationLevelLabelKeys,
  GamificationLevelRow,
  GamificationLevelTranslate,
} from '@communities/common-layer/types/gamification.types'

/**
 * Single home for shared levels-domain helpers: member meta resolution, levels document
 * read gates/labels, and progress math. A future rewards domain gets its own
 * gamification-rewards.utils.ts sibling here. Admin-only helpers (save validation,
 * name payloads, point-source catalog) live in group-settings-layer/utils/ — levels admin
 * helpers specifically in gamification-levels-admin.utils.ts there.
 */

/** Minimal structural shape for gamification meta resolution. */
export interface GamificationMeta {
  level?: number
  points?: number
}

export interface GamificationMetaHolder {
  gamificationMeta?: GamificationMeta
  userGroupData?: { gamificationMeta?: GamificationMeta }
}

/** New members / membership rows with no backend meta start at level 1 with 0 points. */
export const DEFAULT_GAMIFICATION_META: Required<Pick<GamificationMeta, 'points' | 'level'>> = {
  points: 0,
  level: 1,
}

/**
 * When the backend omits usable `gamificationMeta` (missing or empty), treat as level 1 / 0 points.
 * Partial payloads keep provided fields; missing points default to 0, missing level stays unset
 * so callers can still derive from points + ladder.
 */
export function withDefaultGamificationMeta(meta: GamificationMeta | null | undefined): GamificationMeta {
  if (meta == null) return { ...DEFAULT_GAMIFICATION_META }

  const hasLevel = typeof meta.level === 'number'
  const hasPoints = typeof meta.points === 'number'
  if (!hasLevel && !hasPoints) return { ...DEFAULT_GAMIFICATION_META }

  return {
    ...meta,
    points: hasPoints ? meta.points : DEFAULT_GAMIFICATION_META.points,
    ...(hasLevel ? { level: meta.level } : {}),
  }
}

/**
 * Canonical gamificationMeta resolution: the membership-row shape (`userGroupData`) is
 * authoritative and wins over the legacy top-level field when both exist.
 */
export function resolveGamificationMeta(user: GamificationMetaHolder | null | undefined): GamificationMeta | undefined {
  return user?.userGroupData?.gamificationMeta ?? user?.gamificationMeta
}

export interface LevelIds {
  groupId?: string
  locationId?: string
}

export function defaultLevelLabel(level: number): string {
  return `Level ${level}`
}

export function getMinPointsForLevel(level: number, rows?: GamificationLevelRow[]): number {
  if (rows !== undefined) {
    return rows.find(entry => entry.level === level)?.minPoints ?? 0
  }

  return 0
}

export function isMaxLevel(level: number): boolean {
  return level >= GAMIFICATION_LEVEL_COUNT
}

function isValidLevelNumber(level: unknown): level is number {
  return typeof level === 'number' && Number.isInteger(level) && level >= 1 && level <= GAMIFICATION_LEVEL_COUNT
}

function isValidMinPoints(minPoints: unknown): minPoints is number {
  return typeof minPoints === 'number' && Number.isInteger(minPoints) && Number.isFinite(minPoints) && minPoints >= 0
}

function isValidLevelName(name: unknown): name is string | null | undefined {
  return name === null || name === undefined || typeof name === 'string'
}

function readLevelRow(entry: unknown, seenLevels: ReadonlySet<number>): GamificationLevelRow | null {
  if (!entry || typeof entry !== 'object') return null

  const { level, minPoints, name } = entry as Partial<GamificationLevelRow>
  if (!isValidLevelNumber(level) || seenLevels.has(level)) return null
  if (!isValidMinPoints(minPoints) || !isValidLevelName(name)) return null
  if (level === 1 && minPoints !== 0) return null

  return { level, name: name ?? null, minPoints }
}

function hasStrictlyIncreasingMinPoints(rows: GamificationLevelRow[]): boolean {
  const sorted = [...rows].sort((left, right) => left.level - right.level)
  return sorted.every((row, index) => index === 0 || row.minPoints > sorted[index - 1].minPoints)
}

/**
 * Read gate for GET/cache/override paths: exactly nine rows, levels 1–9 once each,
 * integer minPoints, level-1 at 0, strictly increasing thresholds, and string|null names.
 * Name length and other admin-only save rules stay on `validateGamificationLevels` before PUT.
 */
export function isCompleteLevelsDocument(levels: unknown): levels is GamificationLevelRow[] {
  if (!Array.isArray(levels) || levels.length !== GAMIFICATION_LEVEL_COUNT) {
    return false
  }

  const seenLevels = new Set<number>()
  const rows: GamificationLevelRow[] = []

  for (const entry of levels) {
    const row = readLevelRow(entry, seenLevels)
    if (!row) return false

    seenLevels.add(row.level)
    rows.push(row)
  }

  return seenLevels.size === GAMIFICATION_LEVEL_COUNT && hasStrictlyIncreasingMinPoints(rows)
}

export function isCustomLevelName(name: string, level: number): boolean {
  const trimmed = name.trim()
  return Boolean(trimmed) && trimmed !== defaultLevelLabel(level)
}

export function getCustomLevelName(name: string | null | undefined, level: number): string | undefined {
  const trimmed = (name ?? '').trim()
  return isCustomLevelName(trimmed, level) ? trimmed : undefined
}

export function formatGamificationLevelLabel(
  t: GamificationLevelTranslate,
  keys: FormatGamificationLevelLabelKeys,
  level: number,
  rowName: string | null | undefined
): string {
  const customName = getCustomLevelName(rowName, level)
  if (customName) {
    return t(keys.levelWithName, { level, name: customName })
  }
  return t(keys.levelOnly, { level })
}

/** Treat empty / whitespace as missing. Exported so composables reuse one rule. */
export function normalizeId(value: string | undefined): string | undefined {
  const trimmed = value?.trim()
  return trimmed || undefined
}

/**
 * Resolve groupId + locationId for the levels GET.
 * Prefer `primary` (membership / badge / page); use `fallback` only for missing fields
 * (e.g. feed popover → current group when the embed omits ids).
 */
export function pickLevelIds(primary: LevelIds, fallback?: LevelIds): LevelIds {
  return {
    groupId: normalizeId(primary.groupId) ?? normalizeId(fallback?.groupId),
    locationId: normalizeId(primary.locationId) ?? normalizeId(fallback?.locationId),
  }
}

/** Complete ladder + transport error → keep showing ladder. Otherwise: transport error → failed;
 * settled with an incomplete payload → failed. (Pending with no data → not failed.) */
export function isLevelsFailed(levels: GamificationLevelRow[] | undefined, isTransportError: boolean, isPending: boolean): boolean {
  if (isCompleteLevelsDocument(levels)) {
    return false
  }
  return isTransportError || (levels !== undefined && !isPending)
}

/** Complete nine-row document or `[]` (never pad partials with defaults). */
export function toCompleteLevels(levels: GamificationLevelRow[] | undefined): GamificationLevelRow[] {
  return isCompleteLevelsDocument(levels) ? levels : []
}

/** Props-down complete document wins; else fetched complete list or `undefined`. */
export function pickLevels(
  explicit: GamificationLevelRow[] | undefined,
  fetched: GamificationLevelRow[]
): GamificationLevelRow[] | undefined {
  if (isCompleteLevelsDocument(explicit)) {
    return explicit
  }
  return fetched.length > 0 ? fetched : undefined
}

/** TanStack v5 keeps disabled queries "pending" — only load while a fetch can run. */
export function isLevelsPending(canFetch: boolean, hasLevels: boolean, isPending: boolean): boolean {
  return canFetch && !hasLevels && isPending
}

function toProgressPercentage(points: number, currentLevelMinPoints: number, nextLevelMinPoints: number): number {
  const pointsInLevel = points - currentLevelMinPoints
  const pointsForLevel = nextLevelMinPoints - currentLevelMinPoints

  if (pointsForLevel <= 0) return 100

  return Math.min(100, Math.max(0, Math.round((pointsInLevel / pointsForLevel) * 100)))
}

export function calculateLevelProgressPercentage(
  level: number | undefined,
  points: number | undefined,
  rows?: GamificationLevelRow[]
): number {
  if (level === undefined || points === undefined || !rows?.length) return 0
  // Resolve against explicit group rows only — no hardcoded platform defaults fallback.
  if (!rows.some(entry => entry.level === level)) return 0
  if (isMaxLevel(level)) return 100

  const currentLevelMinPoints = getMinPointsForLevel(level, rows)
  const nextLevelMinPoints = getMinPointsForLevel(level + 1, rows)
  return toProgressPercentage(points, currentLevelMinPoints, nextLevelMinPoints)
}

export function calculatePointsToNextLevel(level: number | undefined, points: number | undefined, rows?: GamificationLevelRow[]): number {
  if (level === undefined || points === undefined || !rows?.length) return 0
  if (!rows.some(entry => entry.level === level)) return 0
  if (isMaxLevel(level)) return 0

  const nextLevelMinPoints = getMinPointsForLevel(level + 1, rows)
  return Math.max(0, nextLevelMinPoints - points)
}

/**
 * Highest ladder level whose threshold the points reach (level 1 floor).
 * Used when backend meta.level is empty — not to override a present meta.level.
 */
export function deriveLevelFromPoints(points: number, rows?: GamificationLevelRow[]): number {
  if (!rows?.length) return 1
  // Max qualifying level — independent of API row order (presence/uniqueness only).
  return rows.filter(row => points >= row.minPoints).reduce((highest, row) => Math.max(highest, row.level), 1)
}

/**
 * Display level: prefer `gamificationMeta.level` when set; only derive from points + a
 * complete group ladder when `level` is empty. Does not invent a level when meta is absent.
 */
export function resolveDisplayLevel(meta: GamificationMeta | null | undefined, rows?: GamificationLevelRow[]): number | undefined {
  if (typeof meta?.level === 'number') return meta.level
  if (!meta) return undefined
  if (!isCompleteLevelsDocument(rows)) return undefined
  return deriveLevelFromPoints(meta.points ?? 0, rows)
}

/** True when the meta level is not the level the points imply under this ladder. */
export function isLevelInconsistentWithPoints(level: number, points: number, rows?: GamificationLevelRow[]): boolean {
  return Boolean(rows?.length) && deriveLevelFromPoints(points, rows) !== level
}

/** Group ids whose levels-load failure already toasted this session. */
const shownFor = new Set<string>()

/**
 * Cross-instance toast dedupe: a feed of 20 avatar popovers must not fire 20 identical
 * "levels load failed" toasts when the shared levels query errors.
 * Returns true the first time a group id is marked (caller should toast), false after.
 */
export function markLevelsErrorToastShown(groupId: string): boolean {
  if (shownFor.has(groupId)) {
    return false
  }
  shownFor.add(groupId)
  return true
}

/**
 * Evicts one group's dedupe mark. Callers invoke this when the group's levels document
 * successfully loads/becomes readable again, so a group that fails → recovers → fails
 * re-toasts instead of staying silent for the rest of the session.
 */
export function clearLevelsErrorToastShown(contextKey: string): void {
  shownFor.delete(contextKey)
}

/** Test hook — clears the session dedupe set. */
export function resetLevelsErrorToastDedupe(): void {
  shownFor.clear()
}
